'use client';

import { composeRefs } from '@radix-ui/react-compose-refs';
import { ChevronLeftIcon } from 'lucide-react';
import { m } from 'motion/react';
import * as React from 'react';

import { cn } from '@heidi/utils';
import { Button } from '../../../button';
import { useDesignSystemIntl } from '../../../hooks/use-design-system-intl';

import { usePanelContext } from './panel-context';
import {
  panelMobileBackButtonClasses,
  panelMobileContainerClasses,
  panelMobileViewClasses,
} from './panel.styles';
import type { PanelMobileProps } from './panel.types';

// iOS-style navigation transition
const slideTransition = {
  type: 'tween',
  duration: 0.35,
  ease: [0.32, 0.72, 0, 1], // Standard iOS easing
} as const;

/**
 * Mobile panel component with iOS-style slide navigation between sidebar and panel views.
 * Uses absolutely positioned views that slide in/out independently.
 *
 * Uses the `inert` attribute to:
 * - Prevent focus from reaching the hidden view
 * - Remove hidden view from accessibility tree
 *
 * @example
 * ```tsx
 * <PanelMobile
 *   sidebarContent={<SidebarMenu>...</SidebarMenu>}
 * />
 * ```
 */
// Focusable elements selector for finding first tabbable element
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const PanelMobile = React.forwardRef<HTMLDivElement, PanelMobileProps>(
  ({ 'data-testid': testId, className, sidebarContent, ...props }, ref) => {
    const { mobileView, setMobileView, activePanelContent, activePanelAction } =
      usePanelContext();
    const intl = useDesignSystemIntl();

    // Ref for the container to attach wheel event listener
    const containerRef = React.useRef<HTMLDivElement>(null);
    // Ref for the sidebar view to focus first element when returning from panel
    const sidebarViewRef = React.useRef<HTMLDivElement>(null);
    // Ref for the back button to focus when panel view opens
    const backButtonRef = React.useRef<HTMLButtonElement>(null);

    // Determine which view is active for inert attribute
    const isSidebarView = mobileView === 'sidebar';
    const isPanelView = mobileView === 'panel';

    // Handle back button click
    const handleBackClick = React.useCallback(() => {
      setMobileView('sidebar');
    }, [setMobileView]);

    // Focus back button when panel animation completes
    const handlePanelAnimationComplete = React.useCallback(() => {
      if (isPanelView) {
        backButtonRef.current?.focus();
      }
    }, [isPanelView]);

    // Focus first sidebar element when returning from panel view
    const handleSidebarAnimationComplete = React.useCallback(() => {
      if (isSidebarView) {
        const firstFocusable =
          sidebarViewRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        firstFocusable?.focus();
      }
    }, [isSidebarView]);

    // Block horizontal mouse wheel scrolling
    React.useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const handleWheel = (event: WheelEvent) => {
        // Block horizontal scroll (deltaX !== 0)
        // Allow vertical scroll (deltaY !== 0)
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
          event.preventDefault();
        }
      };

      // Use passive: false to allow preventDefault
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }, []);

    return (
      <div
        ref={composeRefs(ref, containerRef)}
        data-testid={testId}
        data-slot="panel-mobile"
        className={cn(panelMobileContainerClasses, className)}
        {...props}
      >
        {/* Sidebar View - absolutely positioned, slides out to left */}
        <m.div
          className={panelMobileViewClasses}
          initial={false}
          animate={{ x: isPanelView ? '-100%' : '0%' }}
          transition={slideTransition}
          onAnimationComplete={handleSidebarAnimationComplete}
        >
          <div
            ref={sidebarViewRef}
            className="flex size-full flex-col"
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore - inert is a valid HTML attribute but not in React types yet
            inert={isPanelView ? '' : undefined}
            aria-hidden={isPanelView}
          >
            {sidebarContent}
          </div>
        </m.div>

        {/* Panel View - absolutely positioned, slides in from right */}
        <m.div
          className={panelMobileViewClasses}
          initial={false}
          animate={{ x: isPanelView ? '0%' : '100%' }}
          transition={slideTransition}
          onAnimationComplete={handlePanelAnimationComplete}
        >
          <div
            className="flex size-full flex-col"
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore - inert is a valid HTML attribute but not in React types yet
            inert={isSidebarView ? '' : undefined}
            aria-hidden={isSidebarView}
          >
            {/* Header with back button and optional action */}
            <div className={panelMobileBackButtonClasses}>
              <Button
                ref={backButtonRef}
                variant="ghost"
                size="md"
                flush="start"
                onClick={handleBackClick}
              >
                <ChevronLeftIcon />
                {intl.formatMessage({ defaultMessage: 'Back', id: 'cyR7KhiuaU' })}
              </Button>
              {/* Optional action button (e.g., New Chat) - aligned to the right */}
              {activePanelAction && (
                <div className="ms-auto shrink-0">{activePanelAction}</div>
              )}
            </div>

            {/* Panel content - uses overflow-y-auto instead of ScrollArea to allow
                virtualized lists (like VList) to properly calculate viewport height */}
            <div className="flex-1 overflow-y-auto">{activePanelContent}</div>
          </div>
        </m.div>
      </div>
    );
  }
);
PanelMobile.displayName = 'PanelMobile';

export { PanelMobile };
